/**
 * A tolerant HTML reader for the merchant's thank-you page — text in, data out.
 *
 * The NubeSDK bundle runs in a Web Worker: there is no `DOMParser`, no `document`, nothing that
 * turns markup into a tree. So the confirmation page the merchant authored (`thank_you.page_html`)
 * has to be parsed by hand before `htmlTree.ts` can map it onto components.
 *
 * **Deliberately not a spec parser.** No implicit-close rules and no `<table>` fixups, and a named
 * entity table that stops at what editors emit (the numeric forms are decoded in full). It reads
 * the vocabulary the confirmation templates
 * actually use — `div`, `p`, `span`, `h2`, `strong`, `br`, `svg`/`path`/`circle`/`rect` — plus
 * whatever else a merchant plausibly pastes, and it **never throws**: unterminated tags, stray
 * closers and unclosed elements all resolve to something renderable. The caller decides whether the
 * result is good enough, and falls back to `htmlToMarkdown` when it is not.
 *
 * This file stays free of `@tiendanube/*` on purpose — it is string-to-data only, which keeps it
 * testable without a component tree and leaves it eligible for `pure.ts` if the legacy renderer
 * ever needs the same reading.
 */
/** Elements that never have children, so a missing `/` is not an unclosed tag. */
const VOID_TAGS = new Set([
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'param',
    'source',
    'track',
    'wbr',
]);
/**
 * Elements whose content is code, not markup.
 *
 * Dropped wholesale rather than parsed: a `<script>` body is inert here (no DOM to run it in) but
 * its source would otherwise survive as visible text on the buyer's confirmation screen, which is
 * how `htmlToMarkdown` handles it too.
 */
const RAW_TEXT_TAGS = new Set(['script', 'style']);
/**
 * Named character references, beyond the five the XML core defines.
 *
 * 🚨 **The legacy bundle decodes ALL of them and this one has to keep up.** `innerHTML` runs the
 * browser's own entity table, so a merchant's `&times;` is a × for a buyer on Amazonas; every name
 * missing from here reached a Patagonia buyer as the literal text `&times;`, in the middle of their
 * order summary. Reported from a live store on an email-style template — `&nbsp;&times;&nbsp;`
 * between a product name and its quantity — which is exactly the vocabulary a WYSIWYG editor emits.
 *
 * This is the practical set an editor or a pasted email template produces: typography, currency,
 * arrows and the handful of maths symbols. Everything outside it is still covered, because the
 * numeric forms (`&#8212;` / `&#x2014;`) are decoded generically below — so an unlisted NAME is the
 * only thing that survives as text, and it survives unchanged rather than mangled.
 */
const NAMED_ENTITIES = {
    // A plain space rather than U+00A0: U+00A0 is whitespace to JS `\s`, so the converter's
    // run-collapsing would drop it anyway and the two paths would disagree on nothing.
    nbsp: ' ',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    amp: '&',
    // Typography
    hellip: '\u2026',
    mdash: '\u2014',
    ndash: '\u2013',
    bull: '\u2022',
    middot: '\u00b7',
    ldquo: '\u201c',
    rdquo: '\u201d',
    lsquo: '\u2018',
    rsquo: '\u2019',
    sbquo: '\u201a',
    bdquo: '\u201e',
    laquo: '\u00ab',
    raquo: '\u00bb',
    dagger: '\u2020',
    Dagger: '\u2021',
    prime: '\u2032',
    Prime: '\u2033',
    shy: '\u00ad',
    // Symbols
    copy: '\u00a9',
    reg: '\u00ae',
    trade: '\u2122',
    deg: '\u00b0',
    sect: '\u00a7',
    para: '\u00b6',
    permil: '\u2030',
    // Currency
    euro: '\u20ac',
    pound: '\u00a3',
    yen: '\u00a5',
    cent: '\u00a2',
    curren: '\u00a4',
    // Maths — `times` is the one this table was created for.
    times: '\u00d7',
    divide: '\u00f7',
    minus: '\u2212',
    plusmn: '\u00b1',
    frac12: '\u00bd',
    frac14: '\u00bc',
    frac34: '\u00be',
    sup1: '\u00b9',
    sup2: '\u00b2',
    sup3: '\u00b3',
    ne: '\u2260',
    le: '\u2264',
    ge: '\u2265',
    asymp: '\u2248',
    infin: '\u221e',
    // Arrows and marks
    larr: '\u2190',
    uarr: '\u2191',
    rarr: '\u2192',
    darr: '\u2193',
    harr: '\u2194',
    check: '\u2713',
    cross: '\u2717',
    star: '\u2606',
    starf: '\u2605',
    hearts: '\u2665',
};
/** `&name;`, `&#1234;` or `&#x2014;`. Deliberately requires the semicolon — see `decodeEntities`. */
const ENTITY_PATTERN = /&(#\d{1,7}|#[xX][0-9a-fA-F]{1,6}|[a-zA-Z][a-zA-Z0-9]{1,31});/g;
/**
 * Decode character references in `text`.
 *
 * 🚨 **One left-to-right pass, which is what makes the `&amp;` ordering safe.** The old
 * replace-per-entity loop had to run `&amp;` LAST so that `&amp;lt;` decoded to the text `&lt;`
 * rather than to `<` — a rule that is invisible in the data and was already broken in
 * `htmlToMarkdown`'s copy of the table, where `&amp;` sat second. A single `String.replace` never
 * re-scans its own output, so the ordering hazard cannot exist.
 *
 * Unknown names are left exactly as written: a merchant's `&notmyentity;` is far likelier to be
 * their own text than a reference we should guess at, and the legacy path renders it as text too
 * (the browser only decodes names it knows).
 *
 * The semicolon is required. HTML tolerates a few legacy references without one (`&amp` , `&copy`),
 * but so does prose — "Tom & Jerry" would become "Tom ⅁ Jerry" for any name that happens to follow
 * an ampersand — and every editor that emits entities emits the semicolon.
 */
export function decodeEntities(text) {
    if (!text.includes('&'))
        return text;
    return text.replace(ENTITY_PATTERN, (raw, body) => {
        if (body.charAt(0) === '#') {
            const hex = body.charAt(1) === 'x' || body.charAt(1) === 'X';
            const code = Number.parseInt(hex ? body.slice(2) : body.slice(1), hex ? 16 : 10);
            // Surrogates and out-of-range values would throw; a lone NUL is not text either.
            if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff)
                return raw;
            if (code >= 0xd800 && code <= 0xdfff)
                return raw;
            return String.fromCodePoint(code);
        }
        return NAMED_ENTITIES[body] ?? raw;
    });
}
/**
 * The index of the `>` closing the tag that starts at `from`, or -1.
 *
 * Quote-aware, because `indexOf('>')` truncates an attribute that legitimately contains one —
 * `alt="a > b"` would otherwise split mid-tag and spill the remainder as body text.
 */
function findTagEnd(html, from) {
    let quote = '';
    for (let i = from; i < html.length; i++) {
        const ch = html.charAt(i);
        if (quote) {
            if (ch === quote)
                quote = '';
        }
        else if (ch === '"' || ch === "'") {
            quote = ch;
        }
        else if (ch === '>') {
            return i;
        }
    }
    return -1;
}
const ATTR_PATTERN = /([a-zA-Z_:][-\w:.]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'`=<>]+)))?/g;
/** Attribute names lowercased (HTML is case-insensitive); values keep their case, and are decoded. */
function parseAttrs(source) {
    const attrs = {};
    ATTR_PATTERN.lastIndex = 0;
    let match;
    while ((match = ATTR_PATTERN.exec(source))) {
        const name = match[1];
        if (!name)
            continue;
        attrs[name.toLowerCase()] = decodeEntities(match[2] ?? match[3] ?? match[4] ?? '');
    }
    return attrs;
}
/**
 * Parse `html` into a node list. Never throws; malformed input degrades rather than failing.
 *
 * Recovery rules, all chosen so a merchant's typo costs a wrapper rather than the whole page:
 *  - a closing tag with no open match is ignored, not treated as an open;
 *  - a closing tag that matches an ancestor closes everything below it too;
 *  - anything still open at the end is closed implicitly.
 */
export function parseHtml(html) {
    const root = { kind: 'element', tag: '#root', attrs: {}, children: [] };
    const stack = [root];
    const push = (node) => {
        stack[stack.length - 1]?.children.push(node);
    };
    const pushText = (raw) => {
        if (raw)
            push({ kind: 'text', text: decodeEntities(raw) });
    };
    let i = 0;
    while (i < html.length) {
        const lt = html.indexOf('<', i);
        if (lt < 0) {
            pushText(html.slice(i));
            break;
        }
        if (lt > i)
            pushText(html.slice(i, lt));
        if (html.startsWith('<!--', lt)) {
            const end = html.indexOf('-->', lt + 4);
            i = end < 0 ? html.length : end + 3;
            continue;
        }
        // Doctypes and CDATA — structural noise with no visible content.
        if (html.startsWith('<!', lt) || html.startsWith('<?', lt)) {
            const end = findTagEnd(html, lt);
            i = end < 0 ? html.length : end + 1;
            continue;
        }
        const gt = findTagEnd(html, lt);
        if (gt < 0) {
            // A lone `<` in prose ("5 < 6"), or a tag the merchant never closed. Either way it is text.
            pushText(html.slice(lt));
            break;
        }
        const inner = html.slice(lt + 1, gt);
        if (inner.startsWith('/')) {
            const tag = inner.slice(1).trim().toLowerCase();
            for (let depth = stack.length - 1; depth > 0; depth--) {
                if (stack[depth]?.tag === tag) {
                    stack.length = depth;
                    break;
                }
            }
            i = gt + 1;
            continue;
        }
        const selfClosing = inner.endsWith('/');
        const body = selfClosing ? inner.slice(0, -1) : inner;
        const name = /^([a-zA-Z][-\w:]*)/.exec(body)?.[1];
        if (!name) {
            i = gt + 1;
            continue;
        }
        const tag = name.toLowerCase();
        if (RAW_TEXT_TAGS.has(tag)) {
            const close = new RegExp(`</${tag}\\s*>`, 'i').exec(html.slice(gt + 1));
            i = close ? gt + 1 + close.index + close[0].length : html.length;
            continue;
        }
        const element = {
            kind: 'element',
            tag,
            attrs: parseAttrs(body.slice(name.length)),
            children: [],
        };
        push(element);
        if (!selfClosing && !VOID_TAGS.has(tag))
            stack.push(element);
        i = gt + 1;
    }
    return root.children;
}
/**
 * Split a declaration list on `;`, ignoring separators inside quotes or parentheses.
 *
 * A plain `split(';')` corrupts two values the templates and merchants really use: a
 * `url(data:image/png;base64,…)` and any quoted font stack containing a semicolon.
 */
function splitDeclarations(css) {
    const out = [];
    let depth = 0;
    let quote = '';
    let start = 0;
    for (let i = 0; i < css.length; i++) {
        const ch = css.charAt(i);
        if (quote) {
            if (ch === quote)
                quote = '';
        }
        else if (ch === '"' || ch === "'") {
            quote = ch;
        }
        else if (ch === '(') {
            depth++;
        }
        else if (ch === ')') {
            depth = Math.max(0, depth - 1);
        }
        else if (ch === ';' && depth === 0) {
            out.push(css.slice(start, i));
            start = i + 1;
        }
    }
    out.push(css.slice(start));
    return out;
}
/**
 * `background-color` → `backgroundColor`, `-webkit-box-shadow` → `WebkitBoxShadow`.
 *
 * Custom properties keep their exact name — `--brand` is a literal key, and camel-casing it would
 * both break the cascade and lose the declaration.
 */
export function camelCaseProperty(property) {
    const name = property.trim();
    if (name.startsWith('--'))
        return name;
    return name
        .toLowerCase()
        .replace(/-([a-z])/g, (_dash, letter) => letter.toUpperCase())
        .replace(/^ms/, 'ms'); // `-ms-` is the one vendor prefix that stays lowercase in the DOM API.
}
/**
 * An inline `style` attribute as a style object.
 *
 * Values pass through **verbatim**, which is the whole reason this converter is cheap:
 * `NubeComponentStyle` is full csstype `CSS.Properties`, so shorthands (`margin: '0 auto'`,
 * `border: '1px solid #eee'`) and functions (`linear-gradient(...)`) need no expansion.
 *
 * 🚨 `!important` is stripped rather than forwarded. Per `CLAUDE.md`'s measured styling table it is
 * **not a mechanism** in the `style` prop — the host drops the declaration outright, so keeping it
 * turns a working colour into no colour at all. If a rule genuinely has to out-specify the theme,
 * the channel that wins is `styled()`, not this one.
 */
export function parseInlineStyle(css) {
    const style = {};
    if (!css.trim())
        return style;
    for (const declaration of splitDeclarations(css)) {
        const colon = declaration.indexOf(':');
        if (colon < 0)
            continue;
        const property = camelCaseProperty(declaration.slice(0, colon));
        const value = declaration
            .slice(colon + 1)
            .trim()
            .replace(/\s*!\s*important\s*$/i, '')
            .trim();
        if (property && value)
            style[property] = value;
    }
    return style;
}
//# sourceMappingURL=htmlParse.js.map